import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";

export const PayPalButton = ({
  orderId,
  eventId,
  voucherCode,
  createOrderPath = "/api/paypal/create-order",
  captureOrderPath = "/api/paypal/capture-order",
  extraPayload = {},
  onSuccess,
  onError,
}) => {
  const paypalRef = useRef();
  const [sdkReady, setSdkReady] = useState(false);
  const { getIdToken } = useAuth();

  useEffect(() => {
    const clientId = import.meta.env.VITE_PAYPAL_CLIENT_ID;
    const scriptId = "paypal-sdk-script";

    if (window.paypal) {
      setSdkReady(true);
      return;
    }

    if (document.getElementById(scriptId)) {
        // Script is loading or failed
        return;
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD`;
    script.async = true;
    script.onload = () => setSdkReady(true);
    script.onerror = () => {
        console.error("PayPal SDK failed to load");
        if (onError) onError("Failed to load PayPal SDK");
    };
    document.body.appendChild(script);

    return () => {
      // Keep script for other buttons if needed, or remove if solitary
    };
  }, []);

  useEffect(() => {
    if (sdkReady && window.paypal && paypalRef.current) {
        // Clear previous button if any (React strict mode might double render)
        paypalRef.current.innerHTML = "";
        
        window.paypal.Buttons({
            style: {
                layout: 'vertical',
                color: 'gold',
                shape: 'rect',
                label: 'paypal'
            },
            createOrder: async () => {
                try {
                    const token = await getIdToken();
                    const res = await axios.post(`${import.meta.env.VITE_API_BASE_URL}${createOrderPath}`, {
                        orderId,
                        eventId,
                        voucherCode,
                        ...extraPayload,
                    }, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (res.data.id === "FREE" && res.data.status === "COMPLETED") {
                        if (onSuccess) onSuccess(res.data);
                        return "FREE";
                    }
                    return res.data.id;
                } catch (err) {
                    console.error("PayPal Create Order Error:", err);
                    if (onError) onError(err.response?.data?.message || err.message);
                    throw err;
                }
            },
            onApprove: async (data, actions) => {
                try {
                    const token = await getIdToken();
                    const res = await axios.post(`${import.meta.env.VITE_API_BASE_URL}${captureOrderPath}`, {
                        paypalOrderId: data.orderID,
                        orderId,
                        eventId,
                        ...extraPayload,
                    }, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    
                    if (res.data.status === "COMPLETED") {
                        if (onSuccess) onSuccess(res.data);
                    } else {
                        if (onError) onError("Payment failed or not completed");
                    }
                } catch (err) {
                    console.error("PayPal Capture Error:", err);
                    if (onError) onError(err.response?.data?.message || err.message);
                }
            },
            onCancel: () => {
                console.log("PayPal Payment Cancelled");
            },
            onError: (err) => {
                console.error("PayPal Button Error:", err);
                if (onError) onError("An error occurred with PayPal Button");
            }
        }).render(paypalRef.current);
    }
  }, [sdkReady, orderId, eventId, voucherCode, createOrderPath, captureOrderPath, JSON.stringify(extraPayload)]);

  return <div ref={paypalRef} className="w-full min-h-[50px] mb-4"></div>;
};
